import { computed, onScopeDispose, ref, shallowRef, toValue, type MaybeRefOrGetter } from 'vue'
import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { fieldIssueMessage } from '~/services/api/fieldIssueMessage'
import { resolveProblemMessageWithReference } from '~/services/api/resolveProblemMessageWithReference'
import { LegalAgreementEmail } from '~/services/api/contracts/requests/LegalAgreementEmail'
import type { EmailEntryProblem } from '~/composables/EmailEntryProblem'
import { resolveResultMessage } from '~/composables/resolveResultMessage'
import { useLogger } from '~/composables/useLogger'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { LEGAL_AGREEMENT_EMAIL_MAX_RECIPIENTS } from '~/constants/legalAgreementEmail'
import { distinctEmailEntries } from '~/utils/distinctEmailEntries'

const EMAILS_PATH = 'emails'
const EMAIL_ENTRY_PATH = /^emails\.(\d+)$/

/**
 * The "Email this" action of a legal agreement: validates the addresses, sends them and resolves
 * every outcome to presentable state. Field problems land on the address they name
 * (`entryProblems`) or on the list (`listProblem`); every other failure is `actionProblem`.
 */
export const useLegalAgreementEmail = (legalAgreementId: MaybeRefOrGetter<string>) => {
  const legalAgreement = useApiClient().legalAgreement()
  const t = usePresentationTranslator()
  const logger = useLogger('useLegalAgreementEmail')

  const isEmailing = ref(false)
  const isCoolingDown = ref(false)
  const successMessage = shallowRef<() => string>(() => '')
  const actionProblem = shallowRef<() => string>(() => '')
  const listProblem = shallowRef<() => string>(() => '')
  const entryProblems = shallowRef<() => readonly EmailEntryProblem[]>(() => [])
  let cooldownTimer: ReturnType<typeof setTimeout> | undefined

  const startCooldown = (seconds: number): void => {
    clearTimeout(cooldownTimer)
    isCoolingDown.value = true
    cooldownTimer = setTimeout(() => {
      isCoolingDown.value = false
    }, seconds * 1000)
  }
  onScopeDispose(() => {
    clearTimeout(cooldownTimer)
  })

  const resolveListProblem = (problem: AppProblemModel): string => {
    const [issue] = problem.fieldIssues[EMAILS_PATH] ?? []
    if (issue === undefined) return ''
    return issue.code === 'FIELD_REQUIRED'
      ? t('common.legalAgreement.errors.emailsRequired')
      : t('common.legalAgreement.errors.tooManyEmails', {
          max: LEGAL_AGREEMENT_EMAIL_MAX_RECIPIENTS
        })
  }

  const resolveEntryProblems = (
    problem: AppProblemModel,
    emails: readonly string[]
  ): EmailEntryProblem[] =>
    Object.entries(problem.fieldIssues).flatMap(([path, issues]) => {
      const index = EMAIL_ENTRY_PATH.exec(path)?.[1]
      const email = index === undefined ? undefined : emails[Number(index)]
      return email === undefined
        ? []
        : issues.map((issue) => ({ email, message: fieldIssueMessage(issue, t) }))
    })

  const hasUnplacedIssues = (problem: AppProblemModel): boolean =>
    Object.keys(problem.fieldIssues).some(
      (path) => path !== EMAILS_PATH && !EMAIL_ENTRY_PATH.test(path)
    )

  const resolveActionProblem = (problem: AppProblemModel): string =>
    resolveProblemMessageWithReference(problem, t, t('common.legalAgreement.errors.send'))

  /** Places each field issue on the address or list it names; an issue that names neither is the action's. */
  const applyValidationProblem = (problem: AppProblemModel, emails: readonly string[]): void => {
    listProblem.value = () => resolveListProblem(problem)
    entryProblems.value = () => resolveEntryProblems(problem, emails)
    const isPlaced = listProblem.value() !== '' || entryProblems.value().length > 0
    if (hasUnplacedIssues(problem) || !isPlaced)
      actionProblem.value = () => resolveActionProblem(problem)
  }

  /** A rate limit starts the cooldown; every other non-validation failure is the action's problem. */
  const applyServerFailure = (error: unknown, emails: readonly string[]): void => {
    logger.error('send', { error })
    const problem = AppProblemModel.normalize(error)
    if (problem.code === 'VALIDATION_ERROR') {
      applyValidationProblem(problem, emails)
      return
    }
    actionProblem.value = () => resolveActionProblem(problem)
    if (problem.retryDelaySeconds !== null) startCooldown(problem.retryDelaySeconds)
  }

  const send = async (entries: readonly string[]): Promise<void> => {
    const emails = distinctEmailEntries(entries)
    isEmailing.value = true
    successMessage.value = () => ''
    actionProblem.value = () => ''
    listProblem.value = () => ''
    entryProblems.value = () => []
    try {
      const request = LegalAgreementEmail({}).safeParse({ emails })
      if (request.success) {
        const result = await legalAgreement.emailLegalAgreement(
          toValue(legalAgreementId),
          request.data.emails
        )
        successMessage.value = () => resolveResultMessage({ code: result.result_code }, t)
      } else {
        applyValidationProblem(AppProblemModel.fromZodError(request.error), emails)
      }
    } catch (error: unknown) {
      applyServerFailure(error, emails)
    } finally {
      isEmailing.value = false
    }
  }

  return {
    actionProblem: computed(() => actionProblem.value()),
    entryProblems: computed(() => entryProblems.value()),
    isCoolingDown,
    isEmailing,
    listProblem: computed(() => listProblem.value()),
    send,
    successMessage: computed(() => successMessage.value())
  }
}
